import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './Datatable.module.css';

export interface Column {
  key: string;
  label: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  sortable?: boolean;
  render?: (value: any, row: any) => React.ReactNode;
  hideable?: boolean;
}

export interface DataTableProps {
  columns: Column[];
  data: any[];
  onRowSelect?: (row: any) => void;
  selectedRowId?: number | null;
  emptyMessage?: string;
  rowKey?: string;
  showRowSelection?: boolean;
  visibleColumns?: Set<string>;
  onColumnVisibilityChange?: (columnKey: string) => void;
}

const DataTable: React.FC<DataTableProps> = ({
  columns: initialColumns,
  data,
  onRowSelect,
  selectedRowId,
  emptyMessage = 'No data found',
  rowKey = 'id',
  showRowSelection = true,
  visibleColumns: externalVisibleColumns
}) => {
  const [columns, setColumns] = useState(initialColumns);
  const [internalVisibleColumns, setInternalVisibleColumns] = useState<Set<string>>(
    new Set(initialColumns.map(col => col.key))
  );
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  // Use external visible columns if provided, otherwise use internal state
  const visibleColumns = externalVisibleColumns || internalVisibleColumns;

  // Update columns when initial columns change
  useEffect(() => {
    setColumns(initialColumns);
  }, [initialColumns]);

  // Get filtered columns based on visibility
  const filteredColumns = columns.filter(col => visibleColumns.has(col.key));

  // Handle column resize
  const handleMouseDown = (columnKey: string, e: React.MouseEvent) => {
    e.preventDefault();
    setResizingColumn(columnKey);

    const startX = e.pageX;
    const column = columns.find(col => col.key === columnKey);
    const startWidth = column?.width || 150;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const diff = moveEvent.pageX - startX;
      const newWidth = Math.max(
        column?.minWidth || 80,
        Math.min(startWidth + diff, column?.maxWidth || 500)
      );

      setColumns(prev =>
        prev.map(col =>
          col.key === columnKey ? { ...col, width: newWidth } : col
        )
      );
    };

    const handleMouseUp = () => {
      setResizingColumn(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Handle sorting
  const handleSort = (columnKey: string) => {
    const column = columns.find(col => col.key === columnKey);
    if (!column?.sortable) return;

    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig?.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    setSortConfig({ key: columnKey, direction });
  };

  // Sort data
  const sortedData = React.useMemo(() => {
    if (!sortConfig) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === bValue) return 0;

      const comparison = aValue > bValue ? 1 : -1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, sortConfig]);

  // Handle row selection
  const handleRowClick = (row: any) => {
    if (onRowSelect) {
      onRowSelect(row);
    }
  };

  return (
    <div className={styles.tableWrapper}>
      {/* Table */}
      <div className={styles.tableContainer} ref={tableRef}>
        <table className={styles.table}>
          <thead>
            <tr>
              {showRowSelection && (
                <th className={styles.checkboxColumn}>
                  <div className={styles.headerCell}></div>
                </th>
              )}
              {filteredColumns.map((column) => (
                <th
                  key={column.key}
                  style={{
                    width: column.width || 150,
                    minWidth: column.minWidth || 80,
                    maxWidth: column.maxWidth || 500,
                    position: 'relative'
                  }}
                >
                  <div
                    className={`${styles.headerCell} ${column.sortable ? styles.sortable : ''}`}
                    onClick={() => column.sortable && handleSort(column.key)}
                  >
                    <span className={styles.headerLabel}>{column.label}</span>
                    {column.sortable && (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        className={`${styles.sortIcon} ${
                          sortConfig?.key === column.key
                            ? sortConfig.direction === 'asc'
                              ? styles.sortAsc
                              : styles.sortDesc
                            : ''
                        }`}
                      >
                        <path
                          d="M7 15l5 5 5-5M7 9l5-5 5 5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    )}
                  </div>
                  {/* Resize Handle */}
                  <div
                    className={`${styles.resizeHandle} ${
                      resizingColumn === column.key ? styles.resizing : ''
                    }`}
                    onMouseDown={(e) => handleMouseDown(column.key, e)}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td
                  colSpan={filteredColumns.length + (showRowSelection ? 1 : 0)}
                  className={styles.emptyState}
                >
                  <div className={styles.nodata}>

                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M9 11l3 3L22 4"
                      stroke="#9ca3af"
                      strokeWidth="2"
                      strokeLinecap="round"
                      />
                    <path
                      d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"
                      stroke="#9ca3af"
                      strokeWidth="2"
                      />
                  </svg>
                  <p>{emptyMessage}</p>
                      </div>
                </td>
              </tr>
            ) : (
              sortedData.map((row) => (
                <tr
                  key={row[rowKey]}
                  className={selectedRowId === row[rowKey] ? styles.selectedRow : ''}
                  onClick={() => handleRowClick(row)}
                >
                  {showRowSelection && (
                    <td className={styles.checkboxCell}>
                      <input
                        type="radio"
                        aria-label="Select row"
                        name="rowSelection"
                        checked={selectedRowId === row[rowKey]}
                        onChange={() => handleRowClick(row)}
                        className={styles.radio}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                  )}
                  {filteredColumns.map((column) => (
                    <td key={column.key}>
                      {column.render
                        ? column.render(row[column.key], row)
                        : row[column.key] || 'N/A'}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Scroll indicator */}
      <div className={styles.scrollHint}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path
            d="M9 5l7 7-7 7"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
        <span>Scroll horizontally to view all columns</span>
      </div>
    </div>
  );
};

export default DataTable;